import React, { useEffect, useState, useContext } from 'react';
import Snackbar from '@material-ui/core/Snackbar';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { lighten } from '@material-ui/core/styles';
import { AuthContext } from '../../context/Auth/AuthContext';
import SocketWorker from '../../services/SocketWorker';
import api from '../../services/api';
import { BUNDLE_VERSION, fetchServerBundleVersion, isBundleOutdated } from "../../helpers/bundleVersion";

const useStyles = makeStyles((theme) => ({
  snackbar: {
    borderRadius: 8,
    maxWidth: 500,
    margin: theme.spacing(1),
  },
  snackbarContent: {
    backgroundColor: lighten(theme.palette.error.main, 0.85),
    color: theme.palette.error.main,
    borderRadius: theme.borderRadius.medium,
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    padding: theme.spacing(1, 2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row',
    '& .MuiSnackbarContent-message': {
      fontSize: '0.9rem',
      fontWeight: 500,
      marginRight: theme.spacing(1),
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiSnackbarContent-action': {
      marginLeft: 0,
      paddingLeft: 0,
    }
  },
  updateButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    minWidth: 120,
    borderRadius: theme.borderRadius.medium,
    textTransform: 'none',
    fontWeight: 'bold',
    fontSize: '0.85rem',
    padding: theme.spacing(0.5, 1.5),
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  }
}));

const ServiceWorkerUpdate = () => {
  const classes = useStyles();
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const [showReload, setShowReload] = useState(false);
  const [reloadMessage, setReloadMessage] = useState("Nova versão disponível!");
  // eslint-disable-next-line no-unused-vars
  const [socketDisconnectedTime, setSocketDisconnectedTime] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [lastActivityTime, setLastActivityTime] = useState(Date.now());

  // Rastrear atividade do usuário
  useEffect(() => {
    const updateActivity = () => {
      setLastActivityTime(Date.now());
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, []);

  // Monitorar status do socket
  useEffect(() => {
    if (!user?.companyId || !user?.id) return;

    const socketWorker = SocketWorker(user.companyId, user.id);
    let inactivityCheckInterval;
    let socketStatusCheckInterval;

    const unsubscribe = socketWorker.onStatusChange((status) => {
      if (status === 'disconnected') {
        setSocketDisconnectedTime(prev => prev || Date.now());
      } else if (status === 'connected') {
        setSocketDisconnectedTime(null);
      }
    });

    // Verificar inatividade do socket (>2min desconectado)
    socketStatusCheckInterval = setInterval(() => {
      setSocketDisconnectedTime(prev => {
        if (prev) {
          const timeDisconnected = Date.now() - prev;
          if (timeDisconnected > 120000) { // 2 minutos
            setReloadMessage("Conexão perdida há mais de 2 minutos. Recomendamos atualizar a página.");
            setShowReload(true);
          }
        }
        return prev;
      });
    }, 30000); // Verificar a cada 30 segundos

    // Verificar inatividade do usuário (>30min)
    inactivityCheckInterval = setInterval(() => {
      setLastActivityTime(prev => {
        const timeSinceActivity = Date.now() - prev;
        if (timeSinceActivity > 1800000) { // 30 minutos
          setReloadMessage("Você está inativo há mais de 30 minutos. Recomendamos atualizar a página para garantir sincronização.");
          setShowReload(true);
        }
        return prev;
      });
    }, 60000); // Verificar a cada 1 minuto

    return () => {
      if (unsubscribe) unsubscribe();
      if (inactivityCheckInterval) clearInterval(inactivityCheckInterval);
      if (socketStatusCheckInterval) clearInterval(socketStatusCheckInterval);
    };
  }, [user?.companyId, user?.id]);

  // Duas fontes: (1) versão do backend (Versions.versionFrontend) — detecta
  // deploy de backend; (2) build/version.json vs versão compilada — detecta
  // deploy SÓ de frontend (rsync do build), que o gate (1) nunca via.
  useEffect(() => {
    const checkVersion = async () => {
      try {
        const frontendVersion = window.localStorage.getItem("frontendVersion") || '';
        const response = await api.get("/version");
        const backendVersion = response.data?.version || '';

        if (frontendVersion && backendVersion && frontendVersion !== backendVersion) {
          setReloadMessage(`Nova versão disponível! (${backendVersion})`);
          setShowReload(true);
          return;
        }
      } catch (error) {
        // Silenciar erro, não é crítico
        console.debug("Erro ao verificar versão:", error);
      }

      const serverBundle = await fetchServerBundleVersion();
      if (isBundleOutdated(BUNDLE_VERSION, serverBundle)) {
        setReloadMessage("Nova versão disponível!");
        setShowReload(true);
      }
    };

    // Verificar versão a cada 5 minutos
    const versionCheckInterval = setInterval(checkVersion, 5 * 60 * 1000);
    checkVersion(); // Verificar imediatamente

    return () => {
      clearInterval(versionCheckInterval);
    };
  }, []);

  useEffect(() => {
    const lastNotifiedVersion = localStorage.getItem('swLastNotifiedVersion') || '';

    const onSWUpdate = () => {
      const currentVersion = window.localStorage.getItem("frontendVersion") || '';
      if (currentVersion && currentVersion !== lastNotifiedVersion) {
        setReloadMessage("Nova versão disponível!");
        setShowReload(true);
        localStorage.setItem('swLastNotifiedVersion', currentVersion);
      }
    };

    window.addEventListener('serviceWorkerUpdated', onSWUpdate);

    const handleMessage = (event) => {
      if (event.data && event.data.type === 'UPDATE_AVAILABLE') {
        const serviceWorkerVersion = event.data.version || '';
        const currentVersion = serviceWorkerVersion || window.localStorage.getItem("frontendVersion") || '';

        if (currentVersion && currentVersion !== lastNotifiedVersion) {
          setReloadMessage("Nova versão disponível!");
          setShowReload(true);
          localStorage.setItem('swLastNotifiedVersion', currentVersion);
        }
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleMessage);

      const checkInterval = setInterval(() => {
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.getRegistration().then(registration => {
            if (registration) {
              registration.update();
            }
          });
        }
      }, 60 * 60 * 1000);

      return () => {
        window.removeEventListener('serviceWorkerUpdated', onSWUpdate);
        navigator.serviceWorker.removeEventListener('message', handleMessage);
        clearInterval(checkInterval);
      };
    }

    return () => {
      window.removeEventListener('serviceWorkerUpdated', onSWUpdate);
    };
  }, []);

  const reloadPage = () => {
    caches.keys().then(function (names) {
      for (let name of names) caches.delete(name);
    });

    window.location.reload(true);
  };

  return (
    <Snackbar
      open={showReload}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      className={classes.snackbar}
      ContentProps={{
        className: classes.snackbarContent,
      }}
      message={reloadMessage}
      action={
        <Button
          className={classes.updateButton}
          size="small"
          onClick={reloadPage}
        >
          ATUALIZAR
        </Button>
      }
    />
  );
};

export default ServiceWorkerUpdate;